<template>
  <div>
    <NText variant="caption1" weight="bold" class="mr-4">
      {{ t('modules.cash.register_deposit_receipt.register') }}
    </NText>
    <div class="mt-1 w-[23rem]">
      <VeeForm v-slot="{ errors, handleSubmit, meta }" as="">
        <form autocomplete="off" @submit="handleSubmit($event, onSubmit)">
          <div
            class="bg-neutral-secondary rounded-md p-4 max-h-[420px] h-full overflow-scroll"
          >
            <VeeField
              v-slot="{ field, errorMessage }"
              name="brokerBank"
              rules="required"
            >
              <NText
                variant="caption1"
                weight="bold"
                class="text-neutral-secondary-press mr-3"
              >
                {{
                  t(
                    'modules.cash.register_deposit_receipt.broker_account_number'
                  )
                }}
              </NText>

              <div class="mt-2 mb-3">
                <NLoading
                  v-if="isLoadingBrokerBankAccounts"
                  :size="10"
                  class="text-brand-primary"
                />
                <NListbox
                  v-else
                  v-model="formModel.brokerAccount"
                  :placeholder="
                    t('modules.cash.register_deposit_receipt.select')
                  "
                  v-bind="field"
                  input-size="small"
                  :error="errorMessage"
                  show-error
                  required
                  clearable
                >
                  <NListboxOption
                    v-for="item of brokerBankAccounts"
                    :key="item.accountId"
                    class="text-right"
                    :value="item.accountId"
                    :label="item.title"
                  >
                    {{ item.title }}
                  </NListboxOption>
                </NListbox>
                <div v-if="formModel.brokerAccount && generateBankInfo">
                  <RegisterReceiotFormBankInfo
                    :generate-bank-info="generateBankInfo"
                  />
                </div>
              </div>
            </VeeField>

            <NText
              variant="caption1"
              weight="medium"
              class="block text-neutral-secondary-press mr-3"
            >
              {{ t('modules.cash.register_deposit_receipt.upload_file_image') }}
            </NText>

            <input
              ref="file"
              type="file"
              accept="image/png, image/jpeg"
              class="hidden"
              @change="onFileChanged"
            />
            <NBox
              class="flex flex-col justify-center items-center border border-dashed py-8 bg-cover bg-blend-multiply w-80 h-28 px-3 mt-2 mb-3 cursor-pointer"
              :style="previewStyle"
              @click="imageUploadHandler"
            >
              <template v-if="!previewSrc">
                <NText
                  variant="caption1"
                  weight="medium"
                  class="mb-2 text-neutral-secondary-press"
                >
                  +
                  {{
                    t('modules.cash.register_deposit_receipt.upload_file_image')
                  }}
                </NText>
                <NText
                  variant="caption2"
                  weight="regular"
                  class="text-neutral-secondary-press text-center"
                >
                  {{
                    t('modules.cash.register_deposit_receipt.uploadDescription')
                  }}
                </NText>
              </template>
              <div v-else class="text-neutral-primary">
                <NText variant="caption1" weight="medium">
                  {{ t('common.edit') }}
                </NText>
              </div>
            </NBox>

            <NText
              variant="caption1"
              weight="medium"
              class="mt-3 mr-3 text-neutral-secondary-press"
            >
              {{ t('modules.cash.register_deposit_receipt.amount_deposited') }}
            </NText>

            <VeeField
              v-slot="{ field, errorMessage }"
              name="amountDepositedValue"
              :rules="{
                required: true,
                min: [
                  1,
                  t('modules.cash.register_deposit_receipt.amount_deposited'),
                  t('misc.rial'),
                ],
              }"
            >
              <NInput
                v-model.number="formModel.amountDepositedValue"
                v-mask="{
                  alias: 'positiveNumber',
                }"
                class="text-neutral-secondary-press mt-1"
                type="tel"
                input-class="text-center"
                v-bind="field"
                :error="errorMessage || errors.amountDepositedValue"
                :required-sign="false"
                :change-value-on-mouse-wheel="false"
                dir="ltr"
                required
                show-error
                @focusin="$event.target.select()"
              >
                <template #postfix>
                  <NText variant="caption1" class="text-neutral-secondary">
                    {{ t('misc.rial') }}
                  </NText>
                </template>
              </NInput>
              <NText
                :class="{
                  invisible: !formModel.amountDepositedValue,
                }"
                variant="caption1"
                class="block text-center"
              >
                {{ amountDepositedValueWords }}
                {{ t('misc.toman') }}
              </NText>
            </VeeField>

            <NText
              variant="caption1"
              weight="medium"
              class="mt-3 mr-3 text-neutral-secondary-press"
            >
              {{ t('modules.cash.register_deposit_receipt.receipt_number') }}
            </NText>

            <VeeField
              v-slot="{ field, errorMessage }"
              name="receiptNumber"
              :rules="{
                required: true,
              }"
            >
              <NInput
                v-model="formModel.receiptNumber"
                v-mask="{
                  alias: 'integer',
                  placeholder: '',
                  autoUnmask: true,
                  rightAlign: true,
                  allowPlus: false,
                  allowMinus: false,
                }"
                type="tel"
                class="text-neutral-secondary-press mt-1"
                v-bind="field"
                :error="errorMessage"
                show-error
                :change-value-on-mouse-wheel="false"
                dir="ltr"
                @focusin="$event.target.select()"
              />
            </VeeField>

            <NText
              variant="caption1"
              weight="medium"
              class="block mt-3 mr-3 text-neutral-secondary-press"
            >
              {{ t('modules.cash.register_deposit_receipt.request_date') }}
            </NText>

            <NBox class="mb-4 mt-2">
              <NRadioGroup
                v-model="depositedDate"
                :required-sign="false"
                required
              >
                <div
                  class="flex flex-col p-3 border-b border-neutral-secondary"
                >
                  <NRadio
                    :value="today"
                    class="w-full relative"
                    @click="formModel.depositedDate = new Date()"
                  >
                    <div class="flex justify-between text-neutral-primary">
                      <NText
                        variant="caption1"
                        weight="bold"
                        class="text-neutral-secondary-press"
                      >
                        {{ t('misc.today') }}
                      </NText>

                      <NText
                        class="align-middle pl-1 text-neutral-secondary-press"
                        weight="bold"
                        variant="caption1"
                      >
                        {{ d(new Date(), 'shortDate') }}
                      </NText>
                    </div>
                  </NRadio>
                </div>
                <div class="flex flex-col p-3">
                  <NRadio
                    :value="oneDayAgo.getDate()"
                    class="w-full relative"
                    @click="formModel.depositedDate = oneDayAgo"
                  >
                    <div class="flex justify-between text-neutral-primary">
                      <NText
                        variant="caption1"
                        weight="bold"
                        class="text-neutral-secondary-press"
                      >
                        {{ t('misc.select_other_date') }}
                      </NText>
                      <div class="flex items-center">
                        <NText
                          v-if="depositedDate !== today"
                          class="align-middle pl-1 text-neutral-secondary-press"
                          weight="bold"
                          variant="caption1"
                        >
                          {{ d(formModel.depositedDate, 'shortDate') }}
                        </NText>
                        <NegarArrowLeftIcon class="text-neutral-secondary" />
                      </div>
                    </div>
                  </NRadio>
                </div>
              </NRadioGroup>
            </NBox>
            <Datepicker
              v-if="depositedDate !== today"
              v-model="formModel.depositedDate"
              is-local-date-time
            />
          </div>

          <div class="mt-4 w-full">
            <NButton
              v-gid="{
                module: 'cash',
                screen: 'home',
                section: 'depositReceiptDialog',
                component: 'submitButton',
                disabled:
                  !meta.valid ||
                  isRegisterDepositReceipt ||
                  !formModel.imageSrc,
              }"
              variant="fill"
              type="submit"
              color="green"
              class="w-full"
              :loading="isRegisterDepositReceipt"
              :disabled="
                !meta.valid || isRegisterDepositReceipt || !formModel.imageSrc
              "
            >
              {{ t('common.submit_request') }}
            </NButton>
          </div>
        </form>
      </VeeForm>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { num2mixed } from 'num2persian';
import { useToast } from '@negar/core';
import { NegarArrowLeftIcon } from '@negar/icons';
import {
  CASH_QUERY_KEYS,
  HELIUM_REGISTER_RECEIPT_SOURCE,
  UPLOAD_LIMIT,
  useGetBrokerBankAccountsQuery,
  useRegisterDepositReceiptQuery,
  VALID_UPLOAD_TYPE,
} from '@core/cash';
import { toBase64 } from '@libs/utils';
import { useQueryClient } from '@libs/use';

import vMask from '@/directives/mask';

import Datepicker from '@/components/Datepicker/index.vue';
import RegisterReceiotFormBankInfo from './RegisterReceiotFormBankInfo.vue';

const { t, d } = useI18n();
const toast = useToast();

const today = new Date().getDate();
const oneDayAgo = new Date(new Date().setDate(today - 1));

const previewSrc = ref<string | null>(null);
const file = ref();
const isValidImage = ref(true);

const queryClient = useQueryClient();

const depositedDate = ref(today);

const BASE_DEPOSIT_RECEIPT_MODEL = {
  depositedDate: new Date(),
  receiptNumber: null,
  brokerAccount: undefined,
  amountDepositedValue: null,
  imageSrc: '',
  source: HELIUM_REGISTER_RECEIPT_SOURCE,
};

const formModel = reactive({ ...BASE_DEPOSIT_RECEIPT_MODEL });

const amountDepositedValueWords = computed(() => {
  if (formModel.amountDepositedValue) {
    return num2mixed(Math.round(formModel.amountDepositedValue / 10));
  }

  return 0;
});

const { brokerBankAccounts, isLoadingBrokerBankAccounts } =
  useGetBrokerBankAccountsQuery();

const { registerDepositReceipt, isRegisterDepositReceipt } =
  useRegisterDepositReceiptQuery({
    onSuccess(data, { resetFormModel }) {
      toast.neutral({
        message: t(
          'modules.cash.register_deposit_receipt.register_done_message'
        ),
        duration: 3000,
      });

      resetFormModel();
      queryClient.invalidateQueries(CASH_QUERY_KEYS.depositReceipt);
    },
    onReject() {
      toast.negative({
        message: t(
          'modules.cash.register_deposit_receipt.register_failed_message'
        ),
        duration: 3000,
      });
    },
  });

const onFileChanged = (e) => {
  const file = e.target.files[0];

  if (file) {
    if (file?.size > UPLOAD_LIMIT || !VALID_UPLOAD_TYPE.includes(file?.type)) {
      toast.negative({
        message: t(
          'modules.cash.register_deposit_receipt.image_validation_message'
        ),
        duration: 2000,
      });

      isValidImage.value = false;

      return;
    }

    previewSrc.value = URL.createObjectURL(file);

    toBase64(file).then((result) => {
      if (result && typeof result === 'string') {
        formModel.imageSrc = result.replace(/^data:image\/[a-z]+;base64,/, '');
      }
    });

    isValidImage.value = true;
  }
};

const previewStyle = computed(() => {
  if (previewSrc.value) {
    return {
      backgroundImage: `url(${previewSrc.value}), linear-gradient(rgba(0,0,0,0.4),rgba(0,0,0,0.4))`,
    };
  }

  return {};
});

const onSubmit = (model, { resetForm }) => {
  if (!formModel.imageSrc) {
    toast.negative({
      message: t(
        'modules.cash.register_deposit_receipt.image_required_message'
      ),
      duration: 2000,
    });
  } else {
    const resetFormModel = () => {
      Object.assign(formModel, BASE_DEPOSIT_RECEIPT_MODEL);
      resetForm();
      previewSrc.value = null;
      depositedDate.value = today;
      file.value.files = null;
      file.value.value = null;
    };

    registerDepositReceipt({ formModel, resetFormModel });
  }
};

const imageUploadHandler = () => {
  file.value.click();
};
const generateBankInfo = computed(() => {
  if (formModel?.brokerAccount) {
    const brokerAccountItem: Record<string, any> = formModel?.brokerAccount;
    return brokerBankAccounts?.value.filter(
      (item) => brokerAccountItem?.value === item.accountId
    )?.[0];
  } else return null;
});
</script>
